// ══════════════════════════════════════════════════════════════════════════════
//  src/consultorio/ConsultorioPage.jsx
//  EL CONSULTORIO — traes una duda, sales sabiendo
//
//  ── EL RECORRIDO, Y POR QUE ES ASI ──────────────────────────────────────────
//  Escribes tu duda → tres píldoras → cinco ejercicios → «¿te quedó claro?».
//
//  Tres píldoras y no tres páginas. Alguien que abre esto tiene UNA pregunta y
//  a menudo está de pie con el móvil en la mano; tres pantallas de texto es un
//  botón de «atrás». Cada píldora cabe de un vistazo, sin scroll.
//
//  Y después, a prueba. Leer una explicación y sentir que la entendiste es la
//  ilusión más común al estudiar: reconocer no es saber. Los cinco ejercicios
//  son de tipos distintos a propósito — con cinco preguntas de opción múltiple
//  se acierta sin producir una sola frase.
//
//  ── LO QUE PASA SI NO LO ENTIENDE ───────────────────────────────────────────
//  Preguntar «¿te quedó claro?» y no tener respuesta para el «no» es peor que
//  no preguntar. Las cuatro salidas hacen cosas DISTINTAS: más ejemplos, otro
//  ángulo, con qué se conecta, o subir el listón. Si las cuatro devolvieran
//  «lo mismo otra vez», el alumno lo nota a la segunda y deja de pulsar.
// ══════════════════════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    QuizCard, FillBlankCard, WordOrderCard, ErrorDetectCard, MatchingCard,
} from '../engines/EngineMode.jsx';
import { fxHit, fxMiss, fxStart, fxFinish, fxRecord, fxAvanzar, fxTap } from '../audio/fx';
import './consultorio.css';

// El mismo tope que el servidor (`MAX_DUDA`). Se repite aquí a propósito y no se
// pide por API: un contador que parpadea esperando la red es peor que un número
// duplicado. Si algún día cambian, la guardia del examen lo canta.
const MAX_DUDA = 300;
const AVISAR_DESDE = 0.75;

// ── EL TAMAÑO DE LA LETRA LO ELIGE QUIEN LEE ────────────────────────────────
// En una tablet o un portátil, un texto pensado para un móvil de 360px se ve
// diminuto: hay mucho más ancho y la misma letra. Y hay gente que simplemente
// necesita la letra más grande, en cualquier pantalla.
//
// Tres pasos y se recuerda entre sesiones. Multiplica una variable CSS, así que
// crece TODO el bloque de lectura a la vez y no se descuadra nada.
const ZOOMS = [
    { clave: 'normal', escala: 1, rotulo: 'A' },
    { clave: 'grande', escala: 1.18, rotulo: 'A' },
    { clave: 'enorme', escala: 1.38, rotulo: 'A' },
];
const CLAVE_ZOOM = 'nsl_consultorio_zoom_v1';

const EJEMPLOS = [
    'No entiendo el verb to be',
    '¿Cuándo uso present perfect y cuándo past simple?',
    'Los phrasal verbs me confunden',
    'No sé cuándo poner a, an o the',
];

// ── EL RESALTADO ────────────────────────────────────────────────────────────
// El texto llega con **asteriscos** alrededor de la idea que la píldora existe
// para plantar y de cada ejemplo en inglés.
//
// Amarillo como RELLENO con tinta oscura encima, nunca como texto: es la regla
// que ya está escrita en `advanced.css` y la razón es que #ffff00 sobre fondo
// claro no se lee. Aquí se respeta.
function conResaltado(texto) {
    // Se parten los DOBLES primero y los simples dentro de cada trozo. Si se
    // hiciera al reves, `**palabra**` se comeria como dos cursivas vacias y el
    // resaltado desapareceria.
    //
    // Los simples no se pidieron, pero el modelo los usa para enfatizar los
    // ejemplos en inglés. Sin tratarlos, en pantalla salen los asteriscos
    // LITERALES — que es peor que no resaltar nada, porque parece un fallo.
    const salida = [];
    String(texto || '').split('**').forEach((bloque, i) => {
        if (i % 2 === 1) {
            salida.push(<mark key={`m${i}`} className="cns-clave">{bloque}</mark>);
            return;
        }
        bloque.split('*').forEach((t, j) => {
            if (!t) return;
            salida.push(j % 2 === 1
                ? <em key={`e${i}-${j}`} className="cns-enfasis">{t}</em>
                : <span key={`s${i}-${j}`}>{t}</span>);
        });
    });
    return salida;
}

function Pildora({ n, titulo, texto }) {
    return (
        <article className="cns-pildora" style={{ animationDelay: `${n * 90}ms` }}>
            <header className="cns-pildora-cabeza">
                <span className="cns-pildora-num">{n + 1}</span>
                <h3 className="cns-pildora-titulo">{titulo}</h3>
            </header>
            {/* El texto llega con ❌ y ✅ marcando lo incorrecto y lo correcto.
                Se respeta el salto de línea porque esa separación es la que
                impide que alguien memorice la frase equivocada de un vistazo. */}
            <p className="cns-pildora-texto">{conResaltado(texto)}</p>
        </article>
    );
}

export default function ConsultorioPage() {
    const navegar = useNavigate();

    const [duda, setDuda] = useState('');
    const [idioma, setIdioma] = useState('es');
    const [fase, setFase] = useState('preguntar');   // preguntar|cargando|pildoras|practica|cierre
    const [datos, setDatos] = useState(null);
    const [idx, setIdx] = useState(0);
    const [aciertos, setAciertos] = useState(0);
    const [extra, setExtra] = useState(null);
    const [ampliando, setAmpliando] = useState(false);
    const [error, setError] = useState('');
    const [zoom, setZoom] = useState(() => {
        try { return Number(localStorage.getItem(CLAVE_ZOOM)) || 0; } catch { return 0; }
    });
    const arriba = useRef(null);

    const cambiarZoom = (i) => {
        fxTap();
        setZoom(i);
        try { localStorage.setItem(CLAVE_ZOOM, String(i)); } catch { /* modo privado */ }
    };

    const nivel = (() => {
        try { return JSON.parse(localStorage.getItem('user') || '{}')?.level_code || 'B1'; }
        catch { return 'B1'; }
    })();

    const usados = duda.length;
    const cerca = usados >= MAX_DUDA * AVISAR_DESDE;

    // Cada cambio de fase vuelve arriba. Sin esto, al pasar de las píldoras a
    // los ejercicios el alumno aparece a media pantalla y cree que se saltó algo.
    useEffect(() => { arriba.current?.scrollIntoView({ block: 'start' }); }, [fase, idx]);

    const pedir = async () => {
        const q = duda.trim();
        if (q.length < 3) return;
        fxStart();
        setError('');
        setFase('cargando');
        try {
            const r = await fetch('/api/consultorio/consultar', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
                body: JSON.stringify({
                    duda: q.slice(0, MAX_DUDA), nivel, idioma, contexto: '',
                }),
            });
            const d = await r.json().catch(() => null);

            // ── CADA FALLO DICE LO QUE ES ───────────────────────────────
            // Al principio TODO caía en "No pude preparar la explicación", y la
            // primera vez que fallo de verdad el motivo era un 404: el servidor
            // corría con una versión anterior y la ruta no existía todavía.
            // El mensaje decía que había fallado la IA, que es lo único que NO
            // estaba pasando — y así se busca el problema en el sitio
            // equivocado. Un error que miente cuesta más que no tener error.
            if (r.status === 429) {
                setError('Vas muy rápido. Espera un momento y vuelve a intentarlo.');
                setFase('preguntar'); return;
            }
            if (r.status === 404) {
                setError('Esta sección no está disponible en el servidor. '
                    + 'Si estás en local, reinicia el backend.');
                setFase('preguntar'); return;
            }
            if (r.status === 401 || r.status === 403) {
                setError('Tu sesión caducó. Vuelve a entrar y lo retomamos.');
                setFase('preguntar'); return;
            }
            if (r.status === 503) {
                setError('La IA no respondió a tiempo. Inténtalo otra vez.');
                setFase('preguntar'); return;
            }
            if (!r.ok || !d?.ok) {
                setError(d?.motivo === 'fuera_de_alcance'
                    ? 'Eso no es una duda de inglés. Pregúntame algo del idioma y te lo explico.'
                    : 'No pude preparar la explicación. Inténtalo otra vez.');
                setFase('preguntar'); return;
            }
            setDatos(d);
            setIdx(0); setAciertos(0); setExtra(null);
            setFase('pildoras');
        } catch {
            setError('No hay conexión con el servidor.');
            setFase('preguntar');
        }
    };

    const ampliar = async (quiero) => {
        if (!datos || ampliando) return;
        fxTap();
        setAmpliando(true); setExtra(null);
        try {
            const r = await fetch('/api/consultorio/seguir', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
                body: JSON.stringify({ tema: datos.tema, quiero, nivel, idioma }),
            });
            const d = await r.json().catch(() => null);
            if (r.ok && d?.ok) { setExtra(d); fxHit(); }
            else setExtra({ titulo: '', texto: 'No pude ampliarlo ahora. Prueba otra opción.' });
        } catch {
            setExtra({ titulo: '', texto: 'No hay conexión con el servidor.' });
        } finally {
            setAmpliando(false);
        }
    };

    const siguiente = (acerto) => {
        if (acerto) { fxHit(); setAciertos((a) => a + 1); } else { fxMiss(); }
        const total = datos?.ejercicios?.length || 0;
        if (idx < total - 1) setIdx((i) => i + 1);
        else {
            // El mismo criterio que en el curso: lo perfecto suena distinto, o
            // deja de significar algo.
            if (acerto && aciertos + 1 === total) fxRecord(); else fxFinish();
            setFase('cierre');
        }
    };

    const reiniciar = () => {
        fxVolverArriba();
        setDuda(''); setDatos(null); setExtra(null); setError('');
        setFase('preguntar');
    };
    const fxVolverArriba = () => fxAvanzar();

    const tarjeta = (ex) => {
        const props = { exercise: ex, nivel, onCorrect: () => siguiente(true), onWrong: () => siguiente(false) };
        switch (ex?.type) {
            case 'quiz': return <QuizCard {...props} />;
            case 'fill_blank': return <FillBlankCard {...props} />;
            case 'word_order': return <WordOrderCard {...props} />;
            case 'error_detect': return <ErrorDetectCard {...props} />;
            case 'matching': return <MatchingCard {...props} />;
            default: return null;
        }
    };

    return (
        <div className="cns-raiz" style={{ '--cns-escala': ZOOMS[zoom]?.escala ?? 1 }}>
            <div ref={arriba} />

            <header className="cns-cabecera">
                <button className="cns-salir" onClick={() => { fxTap(); navegar('/entrenar'); }}>
                    ← Salir
                </button>
                <div className="cns-cabecera-texto">
                    <h1 className="cns-titulo">El Consultorio</h1>
                    <p className="cns-sub">
                        {datos?.tema ? datos.tema : 'Tu duda, resuelta y puesta a prueba'}
                    </p>
                </div>

                {/* Sólo cuando hay algo que leer. En la pantalla de escribir la
                    duda no hace falta y sería un botón más que descifrar. */}
                {(fase === 'pildoras' || fase === 'cierre') && (
                    <div className="cns-zoom" role="group" aria-label="Tamaño del texto">
                        {ZOOMS.map((z, i) => (
                            <button key={z.clave}
                                className={`cns-zoom-btn ${zoom === i ? 'activo' : ''}`}
                                style={{ fontSize: `${0.7 + i * 0.18}rem` }}
                                aria-pressed={zoom === i}
                                aria-label={`Texto ${z.clave}`}
                                onClick={() => cambiarZoom(i)}>
                                {z.rotulo}
                            </button>
                        ))}
                    </div>
                )}
            </header>

            {/* ── PREGUNTAR ─────────────────────────────────────────────── */}
            {fase === 'preguntar' && (
                <section className="cns-panel">
                    <h2 className="cns-pregunta">¿Qué no te está quedando claro?</h2>
                    <p className="cns-ayuda">
                        Escríbelo como se lo dirías a un amigo. No hace falta que sepas
                        cómo se llama.
                    </p>

                    <textarea
                        className="cns-input"
                        value={duda}
                        maxLength={MAX_DUDA}
                        rows={3}
                        placeholder="Ej: no entiendo el verb to be"
                        aria-label="Escribe tu duda"
                        onChange={(e) => setDuda(e.target.value.slice(0, MAX_DUDA))}
                    />
                    <div className="cns-contador-fila">
                        <span className={`cns-contador ${cerca ? 'cns-contador-cerca' : ''}`} aria-live="polite">
                            {cerca ? `${MAX_DUDA - usados} caracteres restantes` : `${usados}/${MAX_DUDA}`}
                        </span>
                    </div>

                    <div className="cns-ejemplos">
                        {EJEMPLOS.map((e) => (
                            <button key={e} className="cns-ejemplo"
                                onClick={() => { fxTap(); setDuda(e); }}>
                                {e}
                            </button>
                        ))}
                    </div>

                    <div className="cns-idioma">
                        <span className="cns-idioma-rotulo">Explícamelo en</span>
                        <div className="cns-idioma-opciones" role="group" aria-label="Idioma de la explicación">
                            {[['es', 'Español'], ['en', 'English']].map(([v, t]) => (
                                <button key={v}
                                    className={`cns-idioma-btn ${idioma === v ? 'activo' : ''}`}
                                    aria-pressed={idioma === v}
                                    onClick={() => { fxTap(); setIdioma(v); }}>
                                    {t}
                                </button>
                            ))}
                        </div>
                    </div>

                    {error && <p className="cns-error" role="alert">{error}</p>}

                    <button className="cns-primario" onClick={pedir} disabled={duda.trim().length < 3}>
                        Resolver mi duda →
                    </button>
                </section>
            )}

            {/* ── CARGANDO ──────────────────────────────────────────────── */}
            {fase === 'cargando' && (
                <section className="cns-panel cns-cargando">
                    <div className="cns-pulso" aria-hidden="true" />
                    <p className="cns-cargando-texto">Buscando la mejor forma de explicártelo…</p>
                    <p className="cns-ayuda">Tarda unos segundos. Merece la pena.</p>
                </section>
            )}

            {/* ── PÍLDORAS ──────────────────────────────────────────────── */}
            {fase === 'pildoras' && datos && (
                <section className="cns-panel">
                    <div className="cns-pildoras">
                        {datos.pildoras.map((p, i) => (
                            <Pildora key={i} n={i} titulo={p.titulo} texto={p.texto} />
                        ))}
                    </div>
                    <button className="cns-primario"
                        onClick={() => { fxAvanzar(); setFase('practica'); }}>
                        Ponme a prueba →
                    </button>
                    <p className="cns-ayuda cns-centro">
                        {datos.ejercicios.length} ejercicios cortos. Leerlo no es saberlo.
                    </p>
                </section>
            )}

            {/* ── PRÁCTICA ──────────────────────────────────────────────── */}
            {fase === 'practica' && datos && (
                <section className="cns-panel">
                    <div className="cns-progreso" aria-hidden="true">
                        {datos.ejercicios.map((_, i) => (
                            <span key={i} className={`cns-punto ${i < idx ? 'hecho' : i === idx ? 'ahora' : ''}`} />
                        ))}
                    </div>
                    <p className="cns-ayuda cns-centro">
                        {idx + 1} de {datos.ejercicios.length}
                    </p>
                    <div className="cns-ejercicio">{tarjeta(datos.ejercicios[idx])}</div>
                </section>
            )}

            {/* ── CIERRE ────────────────────────────────────────────────── */}
            {fase === 'cierre' && datos && (
                <section className="cns-panel">
                    <div className="cns-marcador">
                        <span className="cns-marcador-num">
                            {aciertos}/{datos.ejercicios.length}
                        </span>
                        <span className="cns-marcador-pie">
                            {aciertos === datos.ejercicios.length ? 'Lo tienes' :
                                aciertos >= datos.ejercicios.length - 1 ? 'Casi entero' : 'Vamos otra vez'}
                        </span>
                    </div>

                    <h2 className="cns-pregunta cns-centro">¿Te quedó claro?</h2>
                    <div className="cns-salidas">
                        {[
                            ['ejemplos', 'Dame más ejemplos', 'Lo entiendo, quiero verlo en uso'],
                            ['otra_via', 'Explícamelo de otra forma', 'No me terminó de encajar'],
                            ['conectar', '¿Con qué se relaciona?', 'Quiero ver dónde encaja'],
                            ['mas_dificil', 'Ponme algo más difícil', 'Esto ya lo tengo'],
                        ].map(([k, t, pie]) => (
                            <button key={k} className="cns-salida" disabled={ampliando}
                                onClick={() => ampliar(k)}>
                                <span className="cns-salida-titulo">{t}</span>
                                <span className="cns-salida-pie">{pie}</span>
                            </button>
                        ))}
                    </div>

                    {ampliando && <p className="cns-ayuda cns-centro">Pensándolo…</p>}
                    {extra && (
                        <article className="cns-pildora cns-extra">
                            {extra.titulo && <h3 className="cns-pildora-titulo">{extra.titulo}</h3>}
                            <p className="cns-pildora-texto">{conResaltado(extra.texto)}</p>
                        </article>
                    )}

                    <button className="cns-secundario" onClick={reiniciar}>
                        Traer otra duda
                    </button>
                </section>
            )}
        </div>
    );
}
